const BOOKINGS_BASE = '/agent/bookings';

export const ENDPOINTS = {
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/agent/logout',
    REGISTER: '/auth/register',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    SET_PASSWORD: '/agent/password/set',
    VERIFY_EMAIL: '/verify-email',
    RESEND_VERIFICATION: '/resend-verification',
  },
  AGENT: {
    AGENT: '/agent',
    REGISTER: '/agent/register',
    GET_ALL: '/agent/all',
    GET_BY_ID: (id: number) => `/agent/${id}`,
    UPDATE: (id: number) => `/agent/update/${id}`,
    LOGIN: '/agent/login',
    REFRESH: '/agent/refresh',
  },
  OPERATORS: {
    GET_ALL: '/agent/operators',
    GET_BY_ID: (id: number) => `/agent/operators/${id}`,
    CREATE: '/agent/operators',
    UPDATE: (id: number) => `/agent/operators/${id}/update`,
    DEACTIVATE: (id: number) => `/agent/operators/${id}/deactivate`,
    REACTIVATE: (id: number) => `/agent/operators/${id}/reactivate`,
    RESEND_INVITATION: (id: number) =>
      `/agent/operators/${id}/resend-invitation`,
    TASKS: (id: number) => `/agent/operators/${id}/tasks`,
    TASK_TRACKING: (assignmentId: number) =>
      `/agent/operators/tasks/${assignmentId}/tracking`,
  },
  ADMIN_USERS: {
    GET_ALL: '/admin-users',
    GET_BY_ID: (id: number) => `/admin-users/${id}`,
    CREATE: '/admin-users',
    UPDATE: (id: number) => `/admin-users/${id}`,
    DELETE: (id: number) => `/admin-users/${id}`,
    DEACTIVATE: (id: number) => `/admin-users/${id}/deactivate`,
    REACTIVATE: (id: number) => `/admin-users/${id}/reactivate`,
    RESEND_INVITATION: (id: number) => `/admin-users/${id}/resend-invitation`,
  },
  PRODUCTS: {
    GET_ALL: '/agent/products',
    GET_ALL_MINIMAL: '/agent/products/minimal',
    GET_ALL_PRICING: '/agent/products/pricing',
    BULK_UPDATE_PRICES: '/agent/products/bulk-update-prices',
    BULK_APPLY_DISCOUNT: '/agent/products/bulk-apply-discount',
    GET_BY_ID: (id: number) => `/agent/products/${id}`,
    DELETE_BY_ID: (id: number) => `/agent/products/${id}`,
    CREATE: '/products/create',
    UPDATE: (id: number) => `/agent/products/${id}/update`,
    DELETE: (id: number) => `/products/delete/${id}`,
    TOGGLE_STATUS: '/agent/products/toggle-status',
    DYNAMIC_FORMS: '/dynamic-forms',
    ATTRIBUTES: '/attributes',
    CATEGORY_RENTAL_TYPES: '/rental-types',
    ADDITIONAL_INFO: '/additional-info',
    OPTIONS: '/options',
    CALENDAR: (id: number) => `/agent/products/${id}/calendar`,
    MARK_DATES_AS_UNAVAILABLE: (id: number) =>
      `/agent/products/${id}/unavailable-dates`,
    STATS: (id: number) => `/agent/products/${id}/stats`,
  },
  CATEGORIES: {
    GET_ALL: '/categories',
    GET_BY_ID: (id: number) => `/categories/${id}`,
  },
  AGENT_CATEGORIES: {
    /** Scoped to the logged-in agent's assigned category (self + active subcategory tree) — never the full catalog. */
    GET_ALL: '/agent/categories',
  },
  ORDERS: {
    GET_ALL: '/orders',
    GET_BY_ID: (id: number) => `/orders/${id}`,
  },
  BOOKINGS: {
    GET_ALL: BOOKINGS_BASE,
    SEARCH_USERS: `${BOOKINGS_BASE}/users`,
    VALIDATE: `${BOOKINGS_BASE}/validate`,
    CREATE_MANUAL: `${BOOKINGS_BASE}/manual`,
    GET_BY_ID: (id: number) => `/bookings/${id}`,
    CREATE: '/bookings/create',
    RESPOND: (id: number) => `${BOOKINGS_BASE}/${id}/status`,
    UPDATE_DETAILS: (id: number) => `${BOOKINGS_BASE}/${id}/details`,
    ASSIGN_OPERATOR: (id: number) => `${BOOKINGS_BASE}/${id}/assign-operator`,
    AVAILABLE_TASKS: (id: number) => `${BOOKINGS_BASE}/${id}/available-tasks`,
    CATEGORY_TASKS: (categoryId: number) =>
      `${BOOKINGS_BASE}/categories/${categoryId}/tasks`,
    GET_INSPECTIONS: (id: number) => `${BOOKINGS_BASE}/${id}/inspections`,
    UPLOAD_DOCUMENT: '/agent/contacts/documents',
    STATS: `${BOOKINGS_BASE}/stats`,
    CHARGE_TYPES: `${BOOKINGS_BASE}/charge-types`,
    ADD_CHARGE: (id: number) => `${BOOKINGS_BASE}/${id}/charges`,
    RESPOND_EXTENSION: (id: number, requestId: number) =>
      `${BOOKINGS_BASE}/${id}/extension-requests/${requestId}/respond`,
  },
  PROMOTIONS: {
    GET_ALL: '/agent/promotions',
    CREATE: '/agent/promotions/create',
    PRODUCT_DETAILS: (id: number) => `/agent/promotions/products/${id}`,
    UPDATE_STATUS: (id: number) => `/agent/promotions/${id}/status`,
    CREDITS: '/agent/promotions/credits',
    GET_ONE: (id: number) => `/agent/promotions/${id}`,
    UPDATE: (id: number) => `/agent/promotions/${id}`,
    WITHDRAW: (id: number) => `/agent/promotions/${id}`,
  },
  NOTIFICATIONS: {
    GET_ALL: '/agent/notifications',
    MARK_AS_READ: (id: string) => `/agent/notifications/${id}/mark-as-read`,
    MARK_ALL_AS_READ: '/agent/notifications/mark-all-as-read',
    DELETE: (id: string) => `/agent/notifications/${id}`,
    UNREAD_COUNT: '/agent/notifications/unread-count',
  },
  BROADCASTING: {
    AUTH: '/broadcasting/auth',
  },
  DASHBOARD: {
    STATISTICS: '/agent/dashboard/statistics',
  },
  LOOKUPS: {
    COUNTRIES: '/lookups/countries',
    CITIES: (countryId: number) => `/lookups/cities/${countryId}`,
  },
  CONTACTS: {
    CREATE: '/agent/contacts',
    GET_ALL: '/agent/contacts',
  },
  OPERATOR_AUTH: {
    LOGIN: '/operator/auth/login',
    LOGOUT: '/operator/auth/logout',
    REFRESH: '/operator/auth/refresh',
    COMPLETE_REGISTRATION: (token: string) =>
      `/operator/complete-registration/${token}`,
  },
  OPERATOR: {
    MY_TASKS: '/operator/my-tasks',
    PROFILE: '/operator/profile',
    UPDATE_TASK_STATUS: (assignmentId: number) =>
      `/operator/tasks/${assignmentId}/status`,
  },
  FEEDBACK: {
    GET_ALL: '/agent/feedbacks',
    GET_STATS: '/agent/feedbacks/stats',
    GET_BY_ID: (id: number) => `/agent/feedbacks/${id}`,
    MARK_AS_READ: '/agent/feedbacks/mark-as-read',
  },
  PROMO_CODES: {
    GET_ALL: '/agent/promo-codes',
    GET_BY_ID: (id: number) => `/agent/promo-codes/${id}`,
    CREATE: '/agent/promo-codes',
    UPDATE: (id: number) => `/agent/promo-codes/${id}`,
    DELETE: (id: number) => `/agent/promo-codes/${id}`,
    UPDATE_STATUS: (id: number) => `/agent/promo-codes/${id}/status`,
    CHECK_CODE: '/agent/promo-codes/check-code',
    GENERATE_CODE: '/agent/promo-codes/generate-code',
  },
  CALENDAR: {
    GET_ALL: '/agent/calendar',
    GET_DAY: '/agent/calendar/day',
  },
  VALIDATE: {
    EMAIL: '/validate/email',
    PHONE: '/validate/phone',
    VAT: '/validate/vat',
    USERNAME: '/validate/username',
  },
  RENTAL_TYPES: {
    GET_ALL: '/products/rental-types',
  },
  OPTIONAL_SERVICE_TYPES: {
    GET_BY_CATEGORY: (categoryId: number) =>
      `/dynamic-forms/categories/${categoryId}/optional-service-types`,
  },
  OPTIONAL_SERVICES: {
    GET_ALL: '/agent/optional-services',
    CREATE: '/agent/optional-services',
    UPDATE: (id: number) => `/agent/optional-services/${id}`,
    DELETE: (id: number) => `/agent/optional-services/${id}`,
  },
  CONTRACT_TEMPLATE: {
    GET: '/agent/contract-template',
    UPDATE: '/agent/contract-template',
    PREVIEW: '/agent/contract-template/preview',
  },
  CANCELLATION_POLICY: {
    GET: '/agent/cancellation-policy',
    UPDATE: '/agent/cancellation-policy',
  },
  TERMS_CONDITIONS: {
    GET: '/agent/terms-conditions',
    UPDATE: '/agent/terms-conditions',
  },
  PROFILE: {
    GET: '/agent/profile',
    UPDATE: '/agent/profile',
  },
  BRANCHES: {
    GET_ALL: '/agent/branches',
    CREATE: '/agent/branches',
    UPDATE: (id: number) => `/agent/branches/${id}`,
    DELETE: (id: number) => `/agent/branches/${id}`,
    SET_MAIN: (id: number) => `/agent/branches/${id}/set-main`,
  },
  PAYMENT_METHODS: {
    GET_ALL: '/agent/payment-methods',
    UPDATE: '/agent/payment-methods',
  },
  CONTRACTS: {
    BY_BOOKING: (bookingId: number) => `/agent/bookings/${bookingId}/contracts`,
    AMEND: (bookingId: number, contractId: number) =>
      `/agent/bookings/${bookingId}/contracts/${contractId}/amend`,
    PDF: (contractId: number) => `/agent/contracts/${contractId}/pdf`,
  },
  SPONSORSHIPS: {
    GET_ALL: '/agent/sponsorships',
    REDEEM: '/agent/sponsorships',
  },
};
